import type { TransferListItem } from '@ibsys/kit';
import type { ReactNode } from 'react';
import type { ExpertReportBlockTreeNode, ExpertReportListItem } from '@/entities/expert-report';
import type { ExpertReportSettings, ExpertReportSettingsColumn } from '@/entities/settings';

/**
 * Минимальная сигнатура локализатора, достаточная для построения подписей элементов Transfer.
 * Любые ключи передаются строкой; узкая типизация i18next (TFunction с проверкой namespace)
 * для этого файла не нужна.
 */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Префиксы ID элементов Transfer для секции экспертных заключений */
export const EXPERT_REPORT_GROUP_PREFIX = '__er_group__:';
export const EXPERT_REPORT_ITEM_PREFIX = '__er__:';

/** Стандартные поля экспертного заключения (порядок отображения в дереве) */
export const STANDARD_FIELDS: { key: ExpertReportSettingsColumn; labelKey: string }[] = [
  { key: 'id', labelKey: 'dialogs:expertReport.id' },
  { key: 'rating', labelKey: 'dialogs:expertReport.total' },
  { key: 'startedAt', labelKey: 'dialogs:dialogListSection.expertReport.startedAt' },
  { key: 'finishedAt', labelKey: 'dialogs:dialogListSection.expertReport.finishedAt' },
  { key: 'status', labelKey: 'dialogs:dialogListSection.expertReport.status' },
];

/**
 * Результат разбора ID элемента секции экспертных заключений.
 * - `field` — стандартное поле отчёта (id/rating/startedAt/finishedAt/status)
 * - `block` — лист дерева блоков (`isHeader = false`)
 * - `parent` — заголовок-разделитель (`isHeader = true`), используется только для каскада
 */
export type ExpertReportItemKind = 'field' | 'block' | 'parent';

export type ParsedExpertReportItemId =
  | { kind: 'field'; reportId: number; field: ExpertReportSettingsColumn }
  | { kind: 'block'; reportId: number; blockId: number }
  | { kind: 'parent'; reportId: number; blockId: number };

/** Возвращает ID группы Transfer для отчёта с указанным ID */
export const getExpertReportGroupId = (reportId: number): string => `${EXPERT_REPORT_GROUP_PREFIX}${reportId}`;

/** Извлекает ID отчёта из ID группы Transfer */
export const parseExpertReportGroupId = (id: string): number | null => {
  if (!id.startsWith(EXPERT_REPORT_GROUP_PREFIX)) return null;
  const reportId = Number.parseInt(id.slice(EXPERT_REPORT_GROUP_PREFIX.length), 10);
  return Number.isNaN(reportId) ? null : reportId;
};

/** Формирует ID Transfer для стандартного поля отчёта */
export const buildFieldItemId = (reportId: number, field: ExpertReportSettingsColumn): string =>
  `${EXPERT_REPORT_ITEM_PREFIX}${reportId}:field:${field}`;

/** Формирует ID Transfer для листового блока отчёта */
export const buildBlockItemId = (reportId: number, blockId: number): string =>
  `${EXPERT_REPORT_ITEM_PREFIX}${reportId}:block:${blockId}`;

/** Формирует ID Transfer для родительского блока (заголовка) — используется только для каскада */
export const buildParentBlockItemId = (reportId: number, blockId: number): string =>
  `${EXPERT_REPORT_ITEM_PREFIX}${reportId}:parent:${blockId}`;

/** Разбирает ID элемента секции экспертных заключений. Возвращает null для прочих ID. */
export const parseExpertReportItemId = (id: string): ParsedExpertReportItemId | null => {
  if (!id.startsWith(EXPERT_REPORT_ITEM_PREFIX)) return null;
  const parts = id.slice(EXPERT_REPORT_ITEM_PREFIX.length).split(':');
  if (parts.length !== 3) return null;
  const [reportIdRaw, kind, valueRaw] = parts;
  const reportId = Number.parseInt(reportIdRaw, 10);
  if (Number.isNaN(reportId)) return null;
  if (kind === 'field') {
    const allowedFields: ExpertReportSettingsColumn[] = ['id', 'rating', 'startedAt', 'finishedAt', 'status'];
    if (!(allowedFields as string[]).includes(valueRaw)) return null;
    return { kind: 'field', reportId, field: valueRaw as ExpertReportSettingsColumn };
  }
  if (kind === 'block' || kind === 'parent') {
    const blockId = Number.parseInt(valueRaw, 10);
    if (Number.isNaN(blockId)) return null;
    return { kind: kind as 'block' | 'parent', reportId, blockId };
  }
  return null;
};

/** Тип элемента плоского списка блоков с указанием уровня вложенности */
type FlatBlockItem = { block: ExpertReportBlockTreeNode; level: number };

/**
 * Преобразует дерево блоков в плоский список с глубиной вложенности.
 * Включает в результат как заголовки (`isHeader = true`), так и листовые блоки.
 */
export const flattenBlocksWithLevel = (
  blocks: ExpertReportBlockTreeNode[],
  level: number = 0,
): FlatBlockItem[] => {
  const result: FlatBlockItem[] = [];
  for (const block of blocks) {
    result.push({ block, level });
    if (block.children?.length) {
      result.push(...flattenBlocksWithLevel(block.children, level + 1));
    }
  }
  return result;
};

/**
 * Возвращает ID всех блоков, у которых есть собственная колонка с баллом в таблице диалогов:
 * листовые блоки поддерева указанного блока (включая сам блок, если он лист) плюс сам блок,
 * если он не лист, но и не заголовок-разделитель (`isHeader`) — тогда он одновременно и группа
 * для детей, и самостоятельный источник данных, и его ID идёт последним (см. {@link buildBlockColumn}
 * в `useDialogDataGridColumns`, где это же правило определяет порядок колонок).
 */
export const collectLeafBlockIds = (block: ExpertReportBlockTreeNode): number[] => {
  if (!block.children?.length) {
    return block.isHeader ? [] : [block.id];
  }
  const childIds = block.children.flatMap((child) => collectLeafBlockIds(child));
  return block.isHeader ? childIds : [...childIds, block.id];
};

/**
 * Возвращает ID всех блоков-заголовков (parent) в поддереве указанного блока (включая сам блок, если он header).
 */
export const collectParentBlockIds = (block: ExpertReportBlockTreeNode): number[] => {
  const result: number[] = [];
  if (block.isHeader) result.push(block.id);
  if (block.children?.length) {
    block.children.forEach((child) => result.push(...collectParentBlockIds(child)));
  }
  return result;
};

/**
 * Карта каскада для блоков-заголовков: ID элемента родителя → массив всех ID-потомков
 * (включая ID других заголовков и листовых блоков), которые должны быть переключены вместе.
 */
export type ExpertReportCascadeMap = Map<string, string[]>;

/** Тип элемента-листа Transfer (группы и сепараторы недопустимы внутри группы) */
type TransferLeafItem = { type?: 'item'; id: string | number; label: ReactNode; disabled?: boolean };

/**
 * Строит элементы Transfer для одной группы «экспертное заключение».
 *
 * Параметры:
 * - `blocksTree` — дерево блоков, если оно уже загружено;
 * - `isLoadingBlocks` — `true`, если по этому отчёту в данный момент идёт сетевой запрос блоков
 *   (только тогда показываем спиннер вместо children).
 */
export const buildExpertReportGroupItem = (
  report: ExpertReportListItem,
  blocksTree: ExpertReportBlockTreeNode[] | undefined,
  isLoadingBlocks: boolean,
  t: TranslateFn,
): TransferListItem => {
  const fieldChildren: TransferLeafItem[] = STANDARD_FIELDS.map(({ key, labelKey }) => ({
    id: buildFieldItemId(report.id, key),
    label: t(labelKey),
  }));

  if (isLoadingBlocks && !blocksTree) {
    return {
      id: getExpertReportGroupId(report.id),
      label: report.name,
      isLoading: true,
      children: fieldChildren,
    };
  }

  if (!blocksTree) {
    // Дерево блоков ещё не запрашивалось (группа не была раскрыта) — показываем только основные поля
    return {
      id: getExpertReportGroupId(report.id),
      label: report.name,
      children: fieldChildren,
    };
  }

  const flatBlocks = flattenBlocksWithLevel(blocksTree);
  const blockChildren: TransferLeafItem[] = flatBlocks.map(({ block }) => {
    if (block.isHeader) {
      return {
        id: buildParentBlockItemId(report.id, block.id),
        label: block.name,
      };
    }
    return {
      id: buildBlockItemId(report.id, block.id),
      label: block.name,
    };
  });

  return {
    id: getExpertReportGroupId(report.id),
    label: report.name,
    children: [...fieldChildren, ...blockChildren],
  };
};

/**
 * Строит карту уровней вложенности (отступов) и каскада для дерева блоков.
 * - levelById: ID элемента Transfer (parent или block) → уровень отступа
 * - cascadeById: ID родительского блока → ID всех его потомков (parent + leaf), которые должны переключаться вместе
 */
export type ExpertReportTreeMaps = {
  /** Уровень отступа (0 — корень) для каждого ID элемента Transfer (parent/block) */
  levelById: Map<string, number>;
  /** Каскад: ID parent-элемента Transfer → ID всех потомков-элементов Transfer */
  cascadeById: ExpertReportCascadeMap;
};

/**
 * Рекурсивно обходит дерево блоков и строит карту уровней + карту каскада для одного отчёта.
 */
export const buildExpertReportTreeMaps = (
  reportId: number,
  blocks: ExpertReportBlockTreeNode[] | undefined,
): ExpertReportTreeMaps => {
  const levelById = new Map<string, number>();
  const cascadeById: ExpertReportCascadeMap = new Map();
  if (!blocks?.length) return { levelById, cascadeById };

  const visit = (node: ExpertReportBlockTreeNode, level: number): string[] => {
    const itemId = node.isHeader ? buildParentBlockItemId(reportId, node.id) : buildBlockItemId(reportId, node.id);
    levelById.set(itemId, level);

    const childIds: string[] = [];
    if (node.children?.length) {
      node.children.forEach((child) => {
        const subIds = visit(child, level + 1);
        childIds.push(...subIds);
      });
    }

    if (node.isHeader) {
      cascadeById.set(itemId, childIds);
    }
    return [itemId, ...childIds];
  };

  blocks.forEach((root) => visit(root, 0));
  return { levelById, cascadeById };
};

/**
 * Преобразует список ID, выбранных в Transfer, в массив настроек по экспертным заключениям.
 * Игнорирует ID родительских блоков и групп — они являются псевдоэлементами для каскада.
 */
export const transferIdsToExpertReportSettings = (ids: string[]): ExpertReportSettings[] => {
  const byReportId = new Map<number, ExpertReportSettings>();

  const ensure = (reportId: number): ExpertReportSettings => {
    let entry = byReportId.get(reportId);
    if (!entry) {
      entry = { id: reportId, columns: [], blockIds: [] };
      byReportId.set(reportId, entry);
    }
    return entry;
  };

  for (const id of ids) {
    const parsed = parseExpertReportItemId(id);
    if (!parsed) continue;
    if (parsed.kind === 'field') {
      const entry = ensure(parsed.reportId);
      if (!entry.columns.includes(parsed.field)) entry.columns.push(parsed.field);
    } else if (parsed.kind === 'block') {
      const entry = ensure(parsed.reportId);
      if (!entry.blockIds.includes(parsed.blockId)) entry.blockIds.push(parsed.blockId);
    }
  }

  return Array.from(byReportId.values()).filter((s) => s.columns.length > 0 || s.blockIds.length > 0);
};

/**
 * Преобразует массив настроек экспертных заключений в список ID Transfer
 * (поля + листовые блоки; псевдоэлементы родительских блоков не добавляются —
 * их «состояние» вычисляется кастомным renderItem'ом).
 */
export const expertReportSettingsToTransferIds = (settings: ExpertReportSettings[]): string[] => {
  const result: string[] = [];
  for (const setting of settings) {
    setting.columns.forEach((field) => result.push(buildFieldItemId(setting.id, field)));
    setting.blockIds.forEach((blockId) => result.push(buildBlockItemId(setting.id, blockId)));
  }
  return result;
};
